import { encodeBoatClass } from '@/network/decoders'
import { allHandicaps, getDefaultHandicap } from '@/orm/catalogs/Handicaps'
import { cannotBeDiscarded } from '@/orm/catalogs/OtherResults'
import BoatClass from '@model/BoatClass'
import Event from '@model/Event'
import EventBoatCategory from '@model/EventBoatCategory'
import EventBoatRace from '@model/EventBoatRace'
import EventClass from '@model/EventClass'
import EventCrew from '@model/EventCrew'
import CountriesRepo from '@repo/CountriesRepo'
import EventRace from '@model/EventRace'
import { Model, useRepo } from 'pinia-orm'
import { BelongsTo, Bool, HasMany, Num, OnDelete, Str, Uid } from 'pinia-orm/decorators'
import { sprintf } from 'sprintf-js'

export default class EventBoat extends Model {
    static entity = 'events-boats'

    @Uid()
    declare id: string
    @Uid()
    declare eventId: string
    @BelongsTo(() => Event, 'eventId')
    declare event: Event
    @Num(0)
    declare boatClassId: number // this is INTENTIONAL, 0 means no boat class assigned but I am checking this in application code
    @BelongsTo(() => BoatClass, 'boatClassId')
    declare boatClass: BoatClass
    @Str('', { notNullable: true })
    declare country: string
    @Str('', { notNullable: true })
    declare sailNumber: string
    @Str(null)
    declare name: string | null
    @Str(null)
    declare detail: string | null
    @HasMany(() => EventCrew, 'eventBoatId') @OnDelete('cascade')
    declare crew: EventCrew[]
    @Num(0)
    declare paid: number | null /* 0 ... not paid, 1 ... paid in advance, 2 ... paid registered in app */
    @Bool(false)
    declare overrideCategories: boolean
    @HasMany(() => EventBoatCategory, 'eventBoatId') @OnDelete('cascade')
    declare manualCategories: EventBoatCategory[]
    @HasMany(() => EventBoatRace, 'eventBoatId') @OnDelete('cascade')
    declare eventBoatRaces: EventBoatRace[]
    @Str(null)
    declare handicapSelected: string | null /* for pylist, RsT, RsA */
    @Num(null)
    declare handicapValue: number | null /* for Zadar, Texel, pynumber */
    /* todo: what for Czech th? */

    static __eventClassMap: Map<string, EventClass | null> = new Map()
    static __countriesRepo: CountriesRepo | null = null

    static countriesRepo() {
        if (EventBoat.__countriesRepo === null) {
            EventBoat.__countriesRepo = useRepo(CountriesRepo)
        }

        return EventBoat.__countriesRepo
    }

    static clearEventClassCache() {
        EventBoat.__eventClassMap.clear()
        EventBoat.__countriesRepo = null
    }

    get dump() {
        return {
            id: this.id,
            boatClassId: encodeBoatClass(this.boatClassId),
            country: this.country,
            sailNumber: this.sailNumber,
            name: this.name,
            detail: this.detail,
            crew: this.crew.map(crew => crew.dump),
            paid: this.paid,
            overrideCategories: this.overrideCategories,
            manualCategories: this.manualCategories.map(c => c.dump),
            eventBoatRaces: this.eventBoatRaces.map(r => r.dump),
            handicapSelected: this.handicapSelected,
            handicapValue: this.handicapValue,
        }
    }

    get handicapDefaultSelected() {
        return this.calcHandicapDefaultSelected()
    }

    calcHandicapDefaultSelected(eventClass = null) {
        return this.handicapSelected
            ?? getDefaultHandicap(eventClass !== null ? eventClass?.handicapSystem : this.eventClass?.handicapSystem)
    }

    set handicapDefaultSelected(val) {
        this.handicapSelected = val
    }

    get handicapDefaultValue() {
        return this.handicapValue ?? parseFloat(getDefaultHandicap(this.eventClass?.handicapSystem))
    }

    set handicapDefaultValue(val) {
        this.handicapValue = this.eventClass?.handicapSystem === 'zadar' ? parseFloat(val) : parseInt(val)
    }

    get handicapShortcut() {
        if (this.eventClass?.handicapSystem === null)
            return ''

        return this.eventClass?.isHandicapSelectable ? this.handicapDefaultSelected : this.handicapDefaultValue
    }

    get handicapCoef() {
        return this.calcHandicapCoef()
    }

    get sailNoFlag() {
        return EventBoat.countriesRepo().iso2ForSailCode(this.country)
    }

    calcHandicapCoef(cachedEventClass = null, raceId = null) {
        let simpleEventClass = cachedEventClass

        if (simpleEventClass === null) {
            simpleEventClass = this.eventClass
        }

        const handicapSystem = simpleEventClass?.handicapSystem

        if (handicapSystem === null || handicapSystem === undefined)
            return 1

        let effectiveHandicapSelect: string | null = null
        let effectiveHandicapValue: number | null = null

        const race = raceId !== null ? useRepo(EventRace).find(raceId) : null
        const isFirstRace = race !== null && race.race === 1

        if (raceId === null || isFirstRace) {
            if (simpleEventClass?.isHandicapSelectable)
                effectiveHandicapSelect = this.calcHandicapDefaultSelected(simpleEventClass)
            else if (simpleEventClass?.isHandicapByValue)
                effectiveHandicapValue = this.handicapDefaultValue
        } else {
            const races = useRepo(EventRace)
                .where('eventId', this.eventId)
                .where((r: EventRace) => r.race <= race.race)
                .orderBy('race', 'desc')
                .get()

            const boatRaces = useRepo(EventBoatRace)
                .where('eventBoatId', this.id)
                .get()

            for (const r of races) {
                const boatRace = boatRaces.find(br => br.eventRaceId === r.id)
                if (boatRace) {
                    if (simpleEventClass?.isHandicapSelectable && boatRace.handicapSelected !== null) {
                        effectiveHandicapSelect = boatRace.handicapSelected
                        break
                    } else if (simpleEventClass?.isHandicapByValue && boatRace.handicapValue !== null) {
                        effectiveHandicapValue = boatRace.handicapValue
                        break
                    }
                }

                if (r.race === 1) {
                    if (simpleEventClass?.isHandicapSelectable)
                        effectiveHandicapSelect = this.calcHandicapDefaultSelected(simpleEventClass)
                    else if (simpleEventClass?.isHandicapByValue)
                        effectiveHandicapValue = this.handicapDefaultValue
                }
            }
        }

        if (simpleEventClass?.isHandicapSelectable) {
            const handicaps = allHandicaps(handicapSystem)?.filter(handicap =>
                handicap.label === effectiveHandicapSelect
            )

            if (handicaps !== null && handicaps !== undefined && handicaps.length >= 1)
                return 1000 / handicaps[0].value

            return 1
        }

        if (simpleEventClass?.isHandicapByValue) {
            const handicapValue = effectiveHandicapValue

            if (handicapValue === null)
                return 1

            if (handicapSystem === 'pynumber')
                return 1000 / handicapValue

            if (handicapSystem === 'texel')
                return 100 / handicapValue

            if (handicapSystem === 'zadar')
                return handicapValue
        }

        return 1
    }

    get isLicenseIssue(): boolean {
        return this.crew.some(c => c.isLicenseIssue)
    }

    get canAddPublicCrew(): boolean {
        const publicMode = this.eventClass?.publicMode ?? 0

        return publicMode > 0 && (this.crew.length >= 1 || publicMode === 2)
    }

    get isValid(): boolean {
        return this.crew !== undefined && this.crew !== null && this.crew.length > 0
            && this.crew.filter(crew => !crew.isValid).length === 0
    }

    get sailNo() {
        return this.country + ' ' + this.sailNumber
    }

    get sailNoSortable() {
        return sprintf('%020d', this.sailNumber) + ' ' + this.country
    }

    get orderedCrewList() {
        return this.crew.sort((
            a,
            b,
        ) => ((a.captain && !b.captain) ? -1 : ((!a.captain && b.captain) ? 1 : a.order - b.order)))
    }

    get eventClassName(): string {
        const evClass = this.eventClass

        return evClass?.eventClassName ?? ''
    }

    get eventClassHandicapSystem(): string | null {
        const evClass = this.eventClass

        return evClass?.handicapSystem ?? null
    }

    get eventClass(): EventClass | null {
        const cacheKey = `${this.eventId}|${this.boatClassId}`
        if (EventBoat.__eventClassMap.has(cacheKey)) {
            return EventBoat.__eventClassMap.get(cacheKey) ?? null
        }

        const found = useRepo(EventClass)
            .where('eventId', this.eventId)
            .where('boatClassId', this.boatClassId)
            .with('boatClass')
            .with('categories')
            .first()

        const result = found ?? null
        EventBoat.__eventClassMap.set(cacheKey, result)

        return result
    }

    get points(): number {
        const eventClass = this.eventClass

        if (eventClass === null)
            return 0

        const totalBoats = eventClass.totalBoats

        const defaultPoints = totalBoats + 1

        return this.event.availableRaces(this.boatClassId)
            .reduce(
                (sum, race) =>
                    sum + (this.isDiscardedRace(race.race) ? 0 : race.boatPoints(this.id, defaultPoints, totalBoats)),
                0,
            )
    }

    get results() {
        return this.cachedResults()
    }

    get categories() {
        const eventClass = this.eventClass

        if (eventClass === null)
            return []

        return eventClass.categories.filter((category) => category.isBoatInCategory(this))
    }

    get orderedCategories() {
        return this.categories.sort((a, b) => (a.visibleShortcut.localeCompare(b.visibleShortcut)))
    }

    cachedResults(availableRaces = null, eventClass = null, totalBoats = null, details = true) {
        if (eventClass === null) {
            eventClass = this.eventClass

            if (eventClass === null)
                return []
        }

        if (totalBoats === null)
            totalBoats = eventClass.totalBoats

        const defaultPoints = totalBoats + 1

        const boatId = this.id

        if (availableRaces === null)
            availableRaces = this.event.availableRaces(this.boatClassId)

        return availableRaces.map(function(race) {
            const result = {
                race: race.race,
                raceId: race.id,
                points: race.boatPoints(boatId, defaultPoints, totalBoats),
                result: race.boatResult(boatId),
                decided: race.boatDecidedResult(boatId),
            }

            if (details)
                result.details = race.boatFullResult(boatId)

            result.validFinish = race.boatIsValidFinish(boatId)

            return result
        })
    }

    get raced() {
        return this.cachedRaced()
    }

    cachedRaced(availableRaces = null) {
        if (availableRaces === null)
            availableRaces = this.event.availableRaces(this.boatClassId)

        return availableRaces.some(race => race.boatIsValidFinish(this.id))
    }

    discardedRaces(discarded = null, availableRaces = null, totalBoats = null, eventClass = null) {
        const resolvedEventClass = eventClass ?? this.eventClass

        if (resolvedEventClass === null || resolvedEventClass === undefined)
            return []

        const maxDiscardCount = discarded ?? resolvedEventClass.discarded
        const resolvedTotalBoats = totalBoats ?? resolvedEventClass.totalBoats
        const fallbackPoints = resolvedTotalBoats + 1
        const races = availableRaces ?? this.event.availableRaces(this.boatClassId)

        type RaceMeta = {
            raceNo: number
            points: number
            discardable: boolean
        }

        const raceMetas: RaceMeta[] = races.map((race: EventRace) => {
            const points = race.boatPoints(this.id, fallbackPoints, resolvedTotalBoats)
            const result = race.boatResult(this.id)
            const discardable = !cannotBeDiscarded.includes(result)

            return {
                raceNo: race.race,
                points,
                discardable,
            }
        })

        const discardableRaces = raceMetas.filter(r => r.discardable)

        discardableRaces
            .sort((a, b) => (a.points === b.points ? a.raceNo - b.raceNo : b.points - a.points))

        return discardableRaces.slice(0, maxDiscardCount).map(race => race.raceNo)
    }

    isDiscardedRace(race) {
        return this.discardedRaces().includes(race)
    }

    get captain(): EventCrew | null {
        const crew = this.crew

        if (crew === null || crew === undefined || crew.length === 0)
            return null

        for (let i = 0; i < crew.length; i++) {
            const member = crew[i]

            if (member.captain)
                return member
        }

        return null
    }

    get sex(): 'male' | 'female' | 'mix' {
        let hasMale = false
        let hasFemale = false

        for (const member of this.crew) {
            const s = member.sex

            if (s === 'male')
                hasMale = true
            else if (s === 'female')
                hasFemale = true

            if (hasMale && hasFemale) {
                return 'mix'
            }
        }

        if (hasFemale) {
            return 'female'
        }

        return 'male'
    }

    get ageOldest(): number {
        let max = -Infinity

        for (const member of this.crew) {
            const a = member.age

            if (a > max)
                max = a
        }

        return max
    }

    get ageYoungest(): number {
        let min = Infinity

        for (const member of this.crew) {
            const a = member.age

            if (a < min)
                min = a
        }

        return min
    }

    get effectiveAgeOldest(): number {
        let max = -Infinity

        for (const member of this.crew) {
            const a = member.effectiveAge

            if (a > max)
                max = a
        }

        return max
    }

    get effectiveAgeYoungest(): number {
        let min = Infinity

        for (const member of this.crew) {
            const a = member.effectiveAge

            if (a < min)
                min = a
        }

        return min
    }

    get isCzech(): boolean {
        for (const member of this.crew) {
            if (!member.isCzech)
                return false
        }

        return true
    }

    isFromRegion(clubId: number | string): boolean {
        for (const member of this.crew) {
            if (!member.isFromRegion(clubId))
                return false
        }

        return true
    }

    isFromDistrict(clubId: number | string): boolean {
        for (const member of this.crew) {
            if (!member.isFromDistrict(clubId))
                return false
        }

        return true
    }

    static piniaOptions = {
        persist: false,
    }
}
